// ---------------------------------------------------------------------------
// Webhook delivery log — shows delivery attempts for a specific endpoint
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { StatusBadge } from './StatusBadge';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { getDebugHeaders } from '../lib/api';
import { useFormatters } from '../hooks/useFormatters';

const apiBase = import.meta.env.VITE_API_BASE_URL || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WebhookDelivery {
  id: string;
  eventType: string;
  status: string;
  attempts: number;
  maxAttempts: number;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

interface WebhookDeliveryLogProps {
  tenantId: string;
  endpointId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WebhookDeliveryLog({ tenantId, endpointId }: WebhookDeliveryLogProps) {
  const f = useFormatters();
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replayingId, setReplayingId] = useState<string | null>(null);

  const loadDeliveries = useCallback(() => {
    setLoading(true);
    setError(null);

    fetch(`${apiBase}/v1/admin/tenants/${tenantId}/webhooks/${endpointId}/deliveries`, {
      headers: { ...getDebugHeaders() },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { data: WebhookDelivery[] };
        setDeliveries(body.data);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load deliveries');
      })
      .finally(() => setLoading(false));
  }, [tenantId, endpointId]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const handleReplay = async (deliveryId: string) => {
    setReplayingId(deliveryId);

    try {
      const response = await fetch(
        `${apiBase}/v1/admin/tenants/${tenantId}/webhooks/${endpointId}/deliveries/${deliveryId}/replay`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': `replay_${deliveryId}_${Date.now()}`,
            ...getDebugHeaders(),
          },
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      // Refresh list after successful replay
      loadDeliveries();
    } catch (err) {
      // Show inline error
      setError(err instanceof Error ? err.message : 'Replay failed');
    } finally {
      setReplayingId(null);
    }
  };

  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState message={error} onRetry={loadDeliveries} />;

  return (
    <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink">Recent Deliveries</h3>
        <button
          onClick={loadDeliveries}
          className="text-sm text-primary hover:text-primary transition-colors"
        >
          Refresh
        </button>
      </div>

      {deliveries.length === 0 ? (
        <div className="px-6 py-8 text-center text-ink-muted text-sm">
          No deliveries recorded for this endpoint.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-hairline" aria-label="Webhook deliveries">
            <thead className="bg-surface-sunken">
              <tr>
                <th className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Attempts
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                  HTTP
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {deliveries.map((d) => (
                <tr key={d.id} className="hover:bg-surface-sunken">
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono text-ink">{d.eventType}</span>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge value={d.status} />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-ink-muted">
                      {d.attempts}/{d.maxAttempts}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-ink-muted">
                      {d.lastStatusCode ?? '--'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-ink-muted tabular-nums font-mono">
                      {f.date(d.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {d.status === 'failed' && (
                      <button
                        onClick={() => handleReplay(d.id)}
                        disabled={replayingId === d.id}
                        className="text-sm text-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {replayingId === d.id ? 'Replaying...' : 'Retry'}
                      </button>
                    )}
                    {d.lastError && (
                      <span
                        className="ms-2 text-xs text-danger truncate block max-w-xs"
                        title={d.lastError}
                      >
                        {d.lastError}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
